import { motion } from 'framer-motion'
import {Moon, Star, Zap, Activity, Shield, Clock} from 'lucide-react'

const stats = [
  { value: '90', unit: 'min', label: 'REM Cycle Science' },
  { value: '6', unit: 'tools', label: 'Diagnostic Modules' },
  { value: '50+', unit: 'KW', label: 'SEO Keywords Covered' },
  { value: '100%', unit: 'free', label: 'No Sign-Up Required' },
]

const features = [
  { icon: Clock, label: 'Wake-Up Calculator', color: 'text-indigo-400' },
  { icon: Moon, label: 'Bedtime Planner', color: 'text-purple-400' },
  { icon: Zap, label: 'Power Nap Timer', color: 'text-amber-400' },
  { icon: Activity, label: 'STOP-BANG Screener', color: 'text-teal-400' },
  { icon: Shield, label: 'CPAP / AHI Tool', color: 'text-sky-400' },
  { icon: Star, label: 'Sleep Debt Tracker', color: 'text-rose-400' },
]

export default function Hero() {
  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16"
      aria-label="Smart Sleep Calculator — Hero Section"
    >
      {/* Cosmic background layers */}
      <div className="absolute inset-0 cosmic-gradient" />
      <div className="absolute inset-0 star-field opacity-60" />

      {/* Orb glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-indigo-600/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full bg-purple-600/8 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-1/3 w-64 h-64 rounded-full bg-teal-500/6 blur-3xl pointer-events-none" />

      {/* Floating moon icon */}
      <motion.div
        className="absolute top-32 right-12 md:right-24 hidden md:block"
        animate={{ y: [0, -12, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      >
        <div className="relative w-24 h-24 opacity-20">
          <Moon size={96} className="text-indigo-300" strokeWidth={0.5} />
        </div>
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-4xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-4 py-1.5 mb-6"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
            <span className="text-xs font-medium text-indigo-300">
              Full Health & Sleep Cycle Diagnostics Suite — Free Online Sleep Calculator
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-tight tracking-tight mb-6"
          >
            <span className="text-white">Calculate Your</span>
            <br />
            <span className="text-gradient-indigo">Perfect Sleep</span>
            <br />
            <span className="text-white text-3xl sm:text-4xl md:text-5xl font-bold">
              Wake-Up Time
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl mb-4"
          >
            The most complete <strong className="text-slate-300">online sleep calculator</strong> — powered by
            90-minute REM cycle science. Get your ideal{' '}
            <strong className="text-slate-300">bedtime calculator by age</strong>, wake up time,
            nap schedule, and clinical sleep disorder screening in one place.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="text-slate-500 text-sm md:text-base max-w-2xl mb-10"
          >
            Trusted tools: <em>sleepyti me bedtime calculator</em> alternative · <em>sleep time calc</em> ·{' '}
            <em>wakeup calculator</em> · <em>stop bang score calculator</em> · <em>circadian sleep calculator</em> ·{' '}
            <em>melatonin jet lag calculator</em>
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <button
              onClick={() => scrollTo('#sleep-calculator')}
              className="btn-primary text-base py-3.5 px-8 glow-indigo flex items-center justify-center gap-2"
              aria-label="Calculate wake up time now"
            >
              <Moon size={18} />
              Calculate Wake-Up Time
            </button>
            <button
              onClick={() => scrollTo('#stop-bang')}
              className="btn-secondary text-base py-3.5 px-8 flex items-center justify-center gap-2"
              aria-label="Take the STOP-BANG sleep apnea screener"
            >
              <Shield size={18} />
              STOP-BANG Screener
            </button>
          </motion.div>

          {/* Feature pills */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-wrap gap-2 mb-16"
          >
            {features.map(f => (
              <button
                key={f.label}
                onClick={() => {
                  const map: Record<string, string> = {
                    'Wake-Up Calculator': '#sleep-calculator',
                    'Bedtime Planner': '#sleep-calculator',
                    'Power Nap Timer': '#nap-planner',
                    'STOP-BANG Screener': '#stop-bang',
                    'CPAP / AHI Tool': '#cpap-ahi',
                    'Sleep Debt Tracker': '#sleep-debt',
                  }
                  scrollTo(map[f.label] || '#sleep-calculator')
                }}
                className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-indigo-500/30 rounded-full px-3 py-1.5 transition-all cursor-pointer group"
              >
                <f.icon size={13} className={`${f.color} group-hover:scale-110 transition-transform`} />
                <span className="text-xs text-slate-400 group-hover:text-slate-300 font-medium">{f.label}</span>
              </button>
            ))}
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl"
          >
            {stats.map(s => (
              <div key={s.label} className="text-center">
                <div className="text-2xl font-black text-white">
                  {s.value}
                  <span className="text-sm font-medium text-indigo-400 ml-1">{s.unit}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5"
      >
        <span className="text-xs text-slate-600">Scroll to explore tools</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-4 h-7 rounded-full border border-slate-700 flex items-center justify-center"
        >
          <div className="w-1 h-2 bg-indigo-400 rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  )
}
